import { Link } from "react-router-dom"
import { Button } from "@/modules/shared/ui/components/button"
import { useContent } from "../hooks/use-content"
import { ArrowUpRight, PlayCircle, CheckCircle } from "lucide-react"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { AppRole } from "@/modules/auth/constants/app-role"

export function HeroHome() {
  const { hero } = useContent()
  const { isAuthenticated, hasRole } = useAuth()

  const dashboardPath = hasRole(AppRole.Employee)
    ? "/employee/dashboard"
    : "/dashboard"
  const primaryCtaHref = isAuthenticated ? dashboardPath : "/signup"

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-[#1A237E] via-[#283593] to-[#1A237E]">
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A237E]/50 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="pt-40 pb-20 sm:pt-44 sm:pb-28 lg:pt-48 lg:pb-32">
          {/* Two column layout: Content + Image */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left column - Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl">
                {hero.heading}
              </h1>

              <p className="mt-6 text-lg sm:text-xl leading-8 text-muted-foreground">
                {hero.subheading}
              </p>

              {/* CTA buttons */}
              <div className="mt-10 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                <Link to={primaryCtaHref}>
                  <Button
                    size="lg"
                    className="group relative rounded-full bg-white hover:bg-slate-100 text-[#1A237E] font-semibold px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  >
                    <span className="flex items-center gap-3">
                      {hero.primaryButton.text}
                      <div className="rounded-full bg-cyan-400 p-2 transition-transform group-hover:translate-x-1">
                        <ArrowUpRight className="w-4 h-4 text-[#1A237E]" />
                      </div>
                    </span>
                  </Button>
                </Link>

                <Button
                  variant="outline"
                  size="lg"
                  className="group rounded-full border-2 border-white/20 bg-white/5 hover:bg-white/10 text-white backdrop-blur-sm px-8 py-6 transition-all duration-300"
                  asChild
                >
                  <Link
                    to="mailto:temperedtalent.com"
                    className="flex items-center gap-2"
                  >
                    <PlayCircle className="w-5 h-5" />
                    {hero.secondaryButton.text}
                  </Link>
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  <span>Free 14-day trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>

            {/* Right column - Compact Product Preview */}
            <div className="relative lg:order-last">
              {/* Floating card style */}
              <div className="relative">
                {/* Subtle glow */}
                <div className="absolute -inset-2 bg-gradient-to-br from-cyan-400/20 via-blue-400/20 to-purple-400/20 rounded-2xl blur-2xl opacity-60" />

                {/* Image container with decorative elements */}
                <div className="relative group">
                  <div className="relative rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 transform transition-transform duration-500 group-hover:scale-[1.02]">
                    <img
                      src={hero.imageSrc}
                      alt={hero.imageAlt}
                      className="w-full h-auto object-cover"
                    />
                    {/* Overlay gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A237E]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* Decorative floating badges */}
                  <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-2 shadow-xl">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-xs font-semibold text-white">
                        Live Preview
                      </span>
                    </div>
                  </div>

                  <div className="absolute -bottom-4 -left-4 bg-cyan-400/90 backdrop-blur-md rounded-xl px-4 py-3 shadow-xl">
                    <div className="text-foreground">
                      <p className="text-xs font-medium"> Fast Setup</p>
                      <p className="text-sm font-bold">Under 5 minutes</p>
                    </div>
                  </div>
                </div>

                {/* Abstract decorative elements */}
                <div className="absolute top-1/4 -right-8 w-16 h-16 bg-cyan-400/10 rounded-full blur-xl animate-pulse" />
                <div className="absolute bottom-1/3 -left-8 w-20 h-20 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-150" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
